import { useState } from 'react';
import { C } from '@ridgewill/brand';
import { Button, Note } from './ui.js';

/**
 * A credential's value, shown the one time it exists in readable form.
 *
 * Shared by Add agent and Rotate, which both hand back a key the auth service
 * keeps only as a hash. One copy of this means one place to get the clipboard
 * fallback right, and the fallback is the part that matters: a value you
 * cannot see again needs a path that does not depend on a browser permission.
 */
export function KeyReveal({ value }: { value: string }) {
  const [copied, setCopied] = useState<boolean | null>(null);

  return (
    <>
      <Note tone="danger">
        The auth service stores only a hash of this key. It cannot be shown again, by this screen
        or any other. If it is lost, rotate it or revoke it.
      </Note>

      <div style={{ marginTop: '.9rem', display: 'flex', gap: '.5rem', alignItems: 'flex-start' }}>
        {/* `userSelect: all` stays even with a Copy button beside it: the
            clipboard API can refuse. */}
        <div
          style={{
            flex: 1,
            padding: '.7rem .8rem',
            background: C.surface,
            border: `1px solid ${C.inputBorder}`,
            borderRadius: 6,
            fontFamily: 'ui-monospace, monospace',
            fontSize: '.85rem',
            wordBreak: 'break-all',
            userSelect: 'all',
          }}
        >
          {value}
        </div>
        <Button
          onClick={() => {
            void navigator.clipboard
              .writeText(value)
              .then(() => setCopied(true))
              .catch(() => setCopied(false));
          }}
        >
          {copied === true ? 'Copied' : 'Copy'}
        </Button>
      </div>

      {copied === false && (
        <Note tone="danger">
          The browser refused the clipboard. Select the key above and copy it manually. It is still
          readable until this dialog closes.
        </Note>
      )}
    </>
  );
}
